import { useState } from 'react'
import { Screen } from '@/components/layout'
import { Button, InlineError, SegmentedControl } from '@/components/primitives'
import { ProgressBar } from '@/components/charts/Bars'
import { DateRangePicker } from '@/components/pickers'
import { ApiFailure, api } from '@/lib/api'
import { t, tServer } from '@/lib/copy'
import { useRoute } from '@/lib/router'

type Format = 'csv' | 'json'
type Scope = 'transactions' | 'analysis' | 'everything'

/** Download transactions and the analysis as CSV or JSON. */
export function ExportScreen() {
  const route = useRoute()
  const [format, setFormat] = useState<Format>('csv')
  const [scope, setScope] = useState<Scope>('transactions')
  const [range, setRange] = useState({ from: '', to: '' })
  const [preparing, setPreparing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const screenState = preparing ? 'preparing' : error ? 'error' : 'ready'

  const run = async () => {
    setPreparing(true)
    setError(null)
    try {
      const result = await api.exportData({
        format,
        scope,
        from: range.from || undefined,
        to: range.to || undefined,
      })
      const link = document.createElement('a')
      link.href = result.url
      link.download = `fintech-${scope}.${format}`
      document.body.append(link)
      link.click()
      link.remove()
    } catch (e) {
      setError(e instanceof ApiFailure ? tServer(e.copyKey, undefined, 'export.error.title') : t('error.generic'))
    } finally {
      setPreparing(false)
    }
  }

  return (
    <Screen id="export" state={screenState} back={{ screen: 'settings' }}>
      <div className="stack" key={route.screen}>
        <SegmentedControl
          options={[
            { value: 'csv' as Format, label: t('export.format.csv') },
            { value: 'json' as Format, label: t('export.format.json') },
          ]}
          value={format}
          onChange={setFormat}
          label={t('export.title')}
        />

        <SegmentedControl
          options={[
            { value: 'transactions' as Scope, label: t('export.scope.transactions') },
            { value: 'analysis' as Scope, label: t('export.scope.analysis') },
            { value: 'everything' as Scope, label: t('export.scope.everything') },
          ]}
          value={scope}
          onChange={setScope}
          label={t('export.title')}
        />

        <DateRangePicker from={range.from} to={range.to} onChange={setRange} />

        {preparing ? (
          <div className="stack-sm">
            <ProgressBar ratio={0.6} label={t('export.preparing')} />
            <span className="caption">{t('export.preparing')}</span>
          </div>
        ) : null}

        {error ? <InlineError>{error}</InlineError> : null}

        <Button variant="primary" onClick={() => void run()} disabled={preparing}>
          {t('export.cta')}
        </Button>
      </div>
    </Screen>
  )
}
